import { useTranslation } from "react-i18next";
import { Box, Button, ButtonGroup, SxProps, Typography, useTheme } from "@mui/material";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type QuickOption<TValue = string> = { label: string; value: TValue; disabled?: boolean };

type QuickOptionButtonGroupProps<TValue extends string | boolean | symbol = string> = {
  options: QuickOption<TValue>[];
  activeValue?: TValue;
  onChange: (option: QuickOption<TValue>) => void;
  ariaLabel?: string;
  isFullWidth?: boolean;
  sx?: SxProps;
};

export default function QuickOptionButtonGroup<TValue extends string | boolean | symbol = string>({
  options,
  activeValue,
  onChange,
  ariaLabel = "Quick options",
  isFullWidth = true,
  sx,
}: QuickOptionButtonGroupProps<TValue>) {
  const { t } = useTranslation("_common");
  const theme = useTheme();

  return (
    <ButtonGroup
      fullWidth={isFullWidth}
      variant="outlined"
      aria-label={ariaLabel}
      disableRipple
      disableFocusRipple
      disableElevation
      sx={sx}
    >
      {options.map((option, index) => {
        const isFirstBtn = index === 0;
        const isLastBtn = index === options.length - 1;
        const isActive = activeValue === option.value;

        return (
          <Button
            key={`quick-option-${String(option.value)}`}
            size="small"
            variant="outlined"
            onClick={() => onChange(option)}
            disableElevation
            disabled={option.disabled}
            sx={[
              {
                position: "relative",
                py: 0.75,
                pl: theme.spacing(3.5),
                pr: theme.spacing(3.5),
                textTransform: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                borderTopLeftRadius: isFirstBtn ? theme.spacing(2) : 0,
                borderBottomLeftRadius: isFirstBtn ? "16px" : 0,
                borderTopRightRadius: isLastBtn ? "16px" : 0,
                borderBottomRightRadius: isLastBtn ? "16px" : 0,
                backgroundColor: isActive ? theme.palette.secondary.main : undefined,
                justifyContent: "center",
                gap: 0,
              },
              ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
              {
                "&:hover, &:focus": {
                  borderColor: sx?.borderColor,
                },
              },
            ]}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {isActive && <FontAwesomeIcon icon={faCheck} size="lg" aria-hidden />}
              <Typography variant="labelLarge" component="span">
                {t(option.label)}
              </Typography>
            </Box>
          </Button>
        );
      })}
    </ButtonGroup>
  );
}
